/** Public site identity used by page meta, JSON-LD, and the HTML sitemap. */
export const SITE = {
  name: 'Quality Air of Texas',
  legalName: 'Quality Air of Texas LLC',
  url: 'https://qualityairofhouston.com',
  phoneDisplay: '(713) 922-5702',
  phoneTel: '+1-713-922-5702',
  email: 'qualityairoftexas@gmail.com',
  street: '6023 Pennworth Ln',
  city: 'Houston',
  region: 'TX',
  postalCode: '77084',
  addressLine: '6023 Pennworth Ln, Houston, TX 77084',
  license: 'TACLB113848E',
  founded: '2015',
  instagram: 'https://www.instagram.com/qualityairin/',
  ogImage: 'https://qualityairofhouston.com/images/about-hero.jpg',
  policyUpdated: 'October 6, 2026',
};

export const SERVICES = [
  {
    slug: 'air-conditioning',
    name: 'Air Conditioning',
    path: '/services/air-conditioning',
    title: 'Air Conditioning Repair & Installation in Houston',
    description:
      'AC repair, replacement, and maintenance for Houston homes. Certified technicians, upfront pricing, and 24/7 emergency cooling service.',
  },
  {
    slug: 'heating',
    name: 'Heating & Furnaces',
    path: '/services/heating',
    title: 'Furnace & Heating Repair in Houston',
    description:
      'Furnace repair, heat pump service, and heating installation for Houston homes. Safe diagnostics and clear pricing before any work begins.',
  },
  {
    slug: 'maintenance',
    name: 'HVAC Maintenance Plans',
    path: '/services/maintenance',
    title: 'HVAC Maintenance Plans in Houston',
    description:
      'Seasonal HVAC tune-ups for Greater Houston homes. Maintenance visits that catch small problems before a breakdown in Texas heat.',
  },
  {
    slug: 'indoor-air-quality',
    name: 'Indoor Air Quality',
    path: '/services/indoor-air-quality',
    title: 'Indoor Air Quality Services in Houston',
    description:
      'Air purifiers, filtration, humidity control, and ventilation for healthier air in Houston homes. Practical options explained up front.',
  },
  {
    slug: 'commercial-hvac',
    name: 'Commercial HVAC',
    path: '/services/commercial-hvac',
    title: 'Commercial HVAC Service in Houston',
    description:
      'Heating and cooling service for Houston businesses. Repairs, maintenance, and system replacements planned around your operating hours.',
  },
];

export const SITEMAP_GROUPS = [
  {
    title: 'Main',
    links: [
      {
        path: '/',
        name: 'Home',
        description: 'Houston HVAC repair, installation, and maintenance from Quality Air of Texas.',
      },
      {
        path: '/services',
        name: 'Services',
        description: 'Air conditioning, heating, maintenance, indoor air quality, and commercial HVAC.',
      },
      {
        path: '/about',
        name: 'About Us',
        description: 'Locally owned Houston HVAC company, founded in 2015 by Pejman Ansari.',
      },
      {
        path: '/testimonials',
        name: 'Testimonials',
        description: 'Reviews from homeowners and businesses we have served around Houston.',
      },
      {
        path: '/blog',
        name: 'HVAC Blog',
        description: 'Practical notes on cooling, heating, and air quality in the Texas climate.',
      },
      {
        path: '/contact',
        name: 'Contact',
        description: 'Call, email, or send a message to schedule service or request an estimate.',
      },
    ],
  },
  {
    title: 'Services',
    links: SERVICES.map((service) => ({
      path: service.path,
      name: service.name,
      description: service.description,
    })),
  },
  {
    title: 'Company',
    links: [
      {
        path: '/card',
        name: 'Business Card',
        description: 'Phone, hours, and a short request form for Quality Air of Texas.',
      },
      {
        path: '/privacy-policy',
        name: 'Privacy Policy',
        description: 'How we collect, use, and protect personal information.',
      },
      {
        path: '/terms-of-service',
        name: 'Terms of Service',
        description: 'Terms for estimates, repairs, installations, payments, and use of this website.',
      },
      {
        path: '/sitemap',
        name: 'Sitemap',
        description: 'Every public page on this website.',
      },
    ],
  },
];

const STATIC_META = {
  '/': {
    title: 'Houston HVAC Repair & Installation | Quality Air of Texas',
    description:
      'Licensed Houston HVAC company for AC repair, heating, maintenance, and indoor air quality. Upfront pricing and 24/7 emergency service.',
    jsonLd: 'home',
  },
  '/services': {
    title: 'HVAC Services in Houston | Quality Air of Texas',
    description:
      'AC repair and installation, furnace service, maintenance plans, indoor air quality, and commercial HVAC across Greater Houston.',
  },
  '/about': {
    title: 'About Quality Air of Texas | Houston HVAC',
    description:
      'Family-owned Houston HVAC company since 2015. Licensed technicians, honest pricing, and service built around comfort and safety.',
  },
  '/testimonials': {
    title: 'Customer Reviews | Quality Air of Texas',
    description:
      'Read what Houston homeowners say about Quality Air of Texas AC repair, furnace installation, and maintenance visits.',
  },
  '/blog': {
    title: 'HVAC Blog | Quality Air of Texas',
    description:
      'Tips for Houston homeowners on air conditioning, heating, maintenance, and indoor air quality from Quality Air of Texas.',
  },
  '/contact': {
    title: 'Contact Quality Air of Texas | Houston HVAC',
    description:
      'Reach Quality Air of Texas at (713) 922-5702 or 6023 Pennworth Ln, Houston, TX 77084. Estimates and 24/7 emergency service.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Quality Air of Texas',
    description:
      'How Quality Air of Texas LLC collects, uses, and protects personal information when you request HVAC service or use our website.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Quality Air of Texas',
    description:
      'Terms for HVAC estimates, repairs, installations, payments, and website use from Quality Air of Texas LLC in Greater Houston.',
  },
  '/sitemap': {
    title: 'Sitemap | Quality Air of Texas',
    description:
      'Browse public pages on Quality Air of Texas, including HVAC services, about, testimonials, the blog, and contact.',
  },
  '/card': {
    title: 'Quality Air of Texas | Houston HVAC',
    description:
      'Call Quality Air of Texas at (713) 922-5702 for AC and heating service in Houston. Hours, address, and a service request form.',
  },
};

const PRIVATE_PREFIXES = [
  '/admin',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/profile-settings',
  '/my-service-requests',
  '/pejman',
  '/pinvoices',
];

export function localBusinessJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HVACBusiness',
    name: SITE.name,
    legalName: SITE.legalName,
    url: `${SITE.url}/`,
    image: `${SITE.url}/images/qat.png`,
    telephone: SITE.phoneTel,
    email: SITE.email,
    foundingDate: SITE.founded,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.street,
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      postalCode: SITE.postalCode,
      addressCountry: 'US',
    },
    areaServed: [
      { '@type': 'City', name: 'Houston' },
      { '@type': 'AdministrativeArea', name: 'Greater Houston' },
    ],
    identifier: {
      '@type': 'PropertyValue',
      name: 'Texas ACR Contractor License',
      value: SITE.license,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '14:00',
      },
    ],
    sameAs: [SITE.instagram],
  };
}

export function packJsonLd(nodes) {
  const list = nodes.filter(Boolean);
  if (list.length === 0) return undefined;
  if (list.length === 1) return list[0];
  return {
    '@context': 'https://schema.org',
    '@graph': list.map((node) => {
      const rest = { ...node };
      delete rest['@context'];
      return rest;
    }),
  };
}

function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE.url}${item.path}`,
    })),
  };
}

export function resolvePageMeta(pathname) {
  if (pathname.startsWith('/blog/') && pathname !== '/blog/') {
    return null;
  }

  if (PRIVATE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    const label = pathname.startsWith('/pejman') ? 'Pejman' : 'Account';
    return {
      title: `${label} | Quality Air of Texas`,
      description: 'Private page for Quality Air of Texas customers and staff.',
      path: pathname,
      noindex: true,
    };
  }

  if (STATIC_META[pathname]) {
    const page = STATIC_META[pathname];
    const crumbs =
      pathname === '/'
        ? null
        : [
            { name: 'Home', path: '/' },
            { name: page.title.split('|')[0].trim(), path: pathname },
          ];
    const graph = [];
    if (page.jsonLd === 'home') graph.push(localBusinessJsonLd());
    if (crumbs) graph.push(breadcrumbJsonLd(crumbs));
    return {
      title: page.title,
      description: page.description,
      path: pathname,
      noindex: false,
      jsonLd: packJsonLd(graph),
    };
  }

  const service = SERVICES.find((item) => item.path === pathname);
  if (service) {
    return {
      title: `${service.title} | Quality Air of Texas`,
      description: service.description,
      path: service.path,
      noindex: false,
      jsonLd: packJsonLd([
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: service.name,
          serviceType: service.title,
          description: service.description,
          url: `${SITE.url}${service.path}`,
          areaServed: { '@type': 'City', name: 'Houston' },
          provider: {
            '@type': 'HVACBusiness',
            name: SITE.name,
            telephone: SITE.phoneTel,
            url: `${SITE.url}/`,
          },
        },
        breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: service.name, path: service.path },
        ]),
      ]),
    };
  }

  return {
    title: 'Page Not Found | Quality Air of Texas',
    description: 'This page is not available on the Quality Air of Texas website.',
    path: pathname,
    noindex: true,
  };
}
